/**
 * Host-neutral client for the per-user FactSet OAuth endpoints exposed by
 * the add-in server under `/api/auth/factset/oauth`.
 *
 * This module owns only wire calls. Host/UI orchestration such as Office
 * dialogs, popup fallback, auto-connect dismissal state, and reconnect
 * single-flight behavior stays in host services.
 */

import { apiFetch } from "../apiClient";
import { readApiError } from "../utils/apiError";
import { FACTSET_ROUTES } from "./factsetConstants";

/**
 * Safe metadata response returned by `GET /api/auth/factset/oauth/session`.
 * Token material is never exposed here — only capability flags, scopes,
 * and grant timestamps that the UI can render without security concerns.
 */
export interface FactSetOAuthSessionData {
  connected: boolean;
  scopes?: string[];
  expiresAt?: number | null;
  grantedAt?: string | null;
  hasConversationalAccess?: boolean;
  hasTextToFormulaAccess?: boolean;
  hasOfficeToolsAccess?: boolean;
  hasIdLookupAccess?: boolean;
}

/**
 * Standard error shape returned by the FactSet OAuth routes. The
 * `reauthorizationUrl` field is the canonical "reconnect" CTA target.
 */
export interface FactSetOAuthErrorPayload {
  error: string;
  message: string;
  service?: string;
  reauthorizationUrl?: string;
}

/**
 * Reads the current FactSet OAuth connection metadata. Returns the safe
 * `FactSetOAuthSessionData` payload, or throws on hard transport errors.
 */
export async function getFactSetOAuthSession(): Promise<FactSetOAuthSessionData> {
  const response = await apiFetch(FACTSET_ROUTES.OAUTH_SESSION, { method: "GET" });
  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
  return response.json();
}

/**
 * Requests a FactSet authorize URL from the server. The caller owns how
 * that URL is opened (Office dialog, popup fallback, or another host UI).
 */
export async function getFactSetOAuthAuthorizationUrl(returnTo?: string): Promise<string> {
  const params = new URLSearchParams({ json: "1" });
  if (returnTo) {
    params.set("returnTo", returnTo);
  }
  const response = await apiFetch(
    `${FACTSET_ROUTES.OAUTH_START}?${params.toString()}`,
    { method: "GET" },
  );
  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
  const data = (await response.json()) as { authorizationUrl?: string };
  if (!data?.authorizationUrl) {
    throw new Error("FactSet OAuth start did not return an authorizationUrl.");
  }
  return data.authorizationUrl;
}

/**
 * Exchanges the authorization code and state returned by the FactSet OAuth
 * callback relay for server-side persisted tokens.
 */
export async function exchangeFactSetOAuthCode(code: string, state: string): Promise<void> {
  const response = await apiFetch(FACTSET_ROUTES.OAUTH_EXCHANGE, {
    method: "POST",
    body: JSON.stringify({ code, state }),
  });
  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
}

/**
 * Clears the user's FactSet OAuth grant from the sealed session. Client-side
 * derived state clearing is owned by higher-level lifecycle orchestration.
 */
export async function disconnectFactSetOAuthGrant(): Promise<void> {
  const response = await apiFetch(FACTSET_ROUTES.OAUTH_DISCONNECT, { method: "POST" });
  if (!response.ok) {
    throw new Error(await readApiError(response));
  }
}
